/**
 * Unity Database Renderer
 * Renders Unity C# database with strongly-typed data classes
 */

import { ExportTemplate } from '@/types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { extractFields } from '../../fieldMapping/extraction';
import { groupItemsByCategory } from '../../fieldMapping/processing';
import { LookupContext } from '../../fieldMapping/references';
import { getCSharpType, formatValueCSharp } from './helpers';

/**
 * Render database template for Unity C# with strongly-typed data classes
 */
export function renderUnityDatabaseTemplate(
  template: ExportTemplate,
  items: any[],
  blueprints: any[],
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[]
): string {
  const categories = groupItemsByCategory(items, blueprints, template.categoryMapping);
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items
  };
  
  // Start building C# file
  let output = '// Auto-generated Database\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System.Collections.Generic;\n\n';
  output += 'namespace Grimoire.Data\n{\n';
  
  // Generate data class for each category
  categories.forEach((category) => {
    // Get fields from first item to define the schema
    if (category.items.length === 0) return;
    
    const firstItem = category.items[0];
    const fields = extractFields(firstItem, template, blueprints, category.name, lookupContext);
    
    // Generate data class
    output += `\tpublic sealed class ${category.name}Data\n\t{\n`;
    
    fields.forEach((field) => {
      // Check for field name override
      let fieldName = applyNamingConvention(field.label, template.namingConventions.fieldName);
      if (template.fieldNameOverrides && firstItem.blueprint_id) {
        const blueprintOverrides = template.fieldNameOverrides[firstItem.blueprint_id];
        if (blueprintOverrides && blueprintOverrides[field.label]) {
          const override = blueprintOverrides[field.label];
          if (override.useExportName && override.exportName) {
            fieldName = override.exportName;
          }
        }
      }
      
      const actualValue = field.value !== undefined ? field.value : field.defaultValue;
      const csharpType = getCSharpType(field.mappedType, actualValue, false, field, blueprints, template.namingConventions.templateName);
      output += `\t\tpublic ${csharpType} ${fieldName};\n`;
    });
    
    output += `\t}\n\n`;
  });
  
  // Generate Database class
  output += '\tpublic static class Database\n\t{\n';
  
  categories.forEach((category) => {
    const dataClassName = `${category.name}Data`;
    
    output += `\t\tpublic static class ${category.name}\n\t\t{\n`;
    output += `\t\t\tpublic static readonly Dictionary<string, ${dataClassName}> Data =\n`;
    output += `\t\t\t\tnew Dictionary<string, ${dataClassName}>\n\t\t\t\t{\n`;
    
    category.items.forEach((item, itemIndex) => {
      const fields = extractFields(item, template, blueprints, category.name, lookupContext);
      const itemKey = applyNamingConvention(item.name || item.id, template.namingConventions.instanceName);
      
      output += `\t\t\t\t\t{\n`;
      output += `\t\t\t\t\t\t"${itemKey}",\n`;
      output += `\t\t\t\t\t\tnew ${dataClassName}\n\t\t\t\t\t\t{\n`;
      
      fields.forEach((field, fieldIndex) => {
        // Check for field name override
        let fieldName = applyNamingConvention(field.label, template.namingConventions.fieldName);
        if (template.fieldNameOverrides && item.blueprint_id) {
          const blueprintOverrides = template.fieldNameOverrides[item.blueprint_id];
          if (blueprintOverrides && blueprintOverrides[field.label]) {
            const override = blueprintOverrides[field.label];
            if (override.useExportName && override.exportName) {
              fieldName = override.exportName;
            }
          }
        }
        
        const actualValue = field.value !== undefined ? field.value : field.defaultValue;
        const fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
        
        output += `\t\t\t\t\t\t\t${fieldName} = ${fieldValue},\n`;
      });
      
      output += `\t\t\t\t\t\t}\n`;
      output += `\t\t\t\t\t},\n`;
    });
    
    output += `\t\t\t\t};\n`;
    output += `\t\t}\n\n`;
  });
  
  output += '\t}\n}\n';
  
  console.log('✅ Unity C# database template rendered successfully with strongly-typed data classes');
  
  return output;
}
